import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { AuthStatus } from './createAuthStore';

interface StatusStore {
  status: AuthStatus;
}

export interface RequireAuthProps {
  children: ReactNode;
  useAuthStore: <T>(selector: (state: StatusStore) => T) => T;
  loginPath?: string;
  accessDeniedPath?: string;
}

export function PlatformRequireAuth({
  children,
  useAuthStore,
  loginPath = '/login',
  accessDeniedPath = '/access-denied'
}: RequireAuthProps) {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'forbidden') {
    return <Navigate to={accessDeniedPath} replace />;
  }

  if (status !== 'authenticated') {
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

export interface GuestOnlyProps {
  children: ReactNode;
  useAuthStore: <T>(selector: (state: StatusStore) => T) => T;
  homePath?: string;
  accessDeniedPath?: string;
}

/** Login route — signed-in users go home; insufficient roles use access-denied. */
export function PlatformGuestOnly({
  children,
  useAuthStore,
  homePath = '/',
  accessDeniedPath = '/access-denied'
}: GuestOnlyProps) {
  const status = useAuthStore((s) => s.status);

  if (status === 'forbidden') {
    return <Navigate to={accessDeniedPath} replace />;
  }

  if (status === 'authenticated') {
    return <Navigate to={homePath} replace />;
  }

  return <>{children}</>;
}

export interface RequireRoleProps<TRole extends string> {
  role: TRole;
  children: ReactNode;
  hasRole: (role: TRole) => boolean;
  roleLabel?: string;
}

/** Gates nested routes when the signed-in user lacks the required App Role (SPA-side UX guard). */
export function PlatformRequireRole<TRole extends string>({
  role,
  children,
  hasRole,
  roleLabel = 'role'
}: RequireRoleProps<TRole>) {
  if (!hasRole(role)) {
    return (
      <div className="route-guard-denied" style={{ padding: '2rem', maxWidth: 560, overflow: 'auto' }}>
        <h2>Access denied</h2>
        <p>
          You don&apos;t have permission to view this page. Required {roleLabel}: {role}.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
